import {
  createRoute,
  createRouter,
  createRootRouteWithContext,
  redirect,
  type ParsedLocation,
  Outlet,
  notFound,
  HeadContent
} from '@tanstack/react-router';
import { NotFoundError } from '@r/page/error/not-found-error.tsx';
import { InternalServerError } from '@r/page/error/intenal-server-error.tsx';
import Layout from '../pages/layout/layout.tsx';
import { AdminDashboard } from '../pages/home/home.tsx';
import UserList from '../pages/users/users.tsx';
import UserInsights from '../pages/users/insights.tsx';
import type {
  RouteRouteReadModel,
  UserMeReadModel,
  WorkspaceWorkspaceWriteModel
} from '@r/api/generated/client.schemas.ts';
import { User } from '@admin/pages/users/user.tsx';
import UserImport from '@admin/pages/users/import.tsx';
import { Company } from '@admin/pages/companies/company.tsx';
import CompanyList from '@admin/pages/companies/companies.tsx';
import CompaniesImport from '@admin/pages/companies/import.tsx';
import CompaniesModify from '@admin/pages/companies/modify.tsx';
import PointsImport from '@admin/pages/points/import.tsx';
import Auth from '@admin/pages/auth/auth.tsx';
import { ChangePassword } from '@admin/pages/auth/change-password.tsx';
import ResetPassword from '@admin/pages/reset-password/reset-password.tsx';
import ResetPasswordCallback from '@admin/pages/reset-password/reset-password-callback.tsx';
import { Points } from '@admin/pages/points/points.tsx';
import { PublicLayoutComponent } from '@r/components/router/public-layout-component.tsx';
import { lazy } from 'react';
import { PerformancesInsights } from '@admin/pages/performances/insights.tsx';
import PerformancesImport from '@admin/pages/performances/import.tsx';
import { ProductsReferences } from '@admin/pages/performances/products-references.tsx';
import { Targets } from '@admin/pages/performances/targets.tsx';
import { Steps } from '@admin/pages/performances/steps.tsx';
import ApiToken from '@admin/pages/api/token.tsx';
import { ApiTokenDoc } from '@admin/components/api/doc/api-token-doc.tsx';
import { Typologies } from '@admin/pages/typologies/typologies.tsx';
import Segments from '@admin/pages/segments/segments.tsx';
import { Hierarchy } from '@admin/pages/hierarchy/hierarchy.tsx';
import { HierarchyDetail } from '@admin/pages/hierarchy/hierarchy-detail.tsx';
import SegmentEdit from '@admin/pages/segments/segment-edit.tsx';
import { CreateWorkspaceStep1 } from '@admin/pages/workspace/create-workspace-step-1.tsx';
import { CreateWorkspaceStep2 } from '@admin/pages/workspace/create-workspace-step-2.tsx';
import { CreateWorkspaceStep3 } from '@admin/pages/workspace/create-workspace-step-3.tsx';
import { CreateWorkspaceStep4 } from '@admin/pages/workspace/create-workspace-step-4.tsx';
import {
  CreateWorkspaceLayout,
  getStepValidation
} from '@admin/pages/workspace/create-workspace-layout.tsx';
import CompanyInsights from '@admin/pages/companies/insights.tsx';
import ScheduledTasks from '@admin/pages/scheduled-tasks/scheduled-tasks.tsx';
import Shops from '@admin/pages/shops/shops.tsx';
import { Shop } from '@admin/pages/shops/shop.tsx';
import { MyAccount } from '@admin/pages/my-account/my-account.tsx';
import TargetsImport from '@admin/pages/companies/targets-import.tsx';
import { Translations } from '@admin/pages/translations/translations.tsx';
import { Order } from '@r/components/order/order-detail.tsx';
import { OrderRewardDetail } from '@admin/pages/orders/order-reward-detail.tsx';
import { Orders } from '@admin/pages/orders/orders.tsx';
import { FiddyAdminDashboard } from '@admin/pages/fiddy-admin/fiddy-admin.tsx';
import { AdminTranslations } from '@admin/pages/translations/admin-translations.tsx';
import OrdersInsights from '@admin/pages/orders/insights.tsx';
import {
  ContentBlockEditPage,
  ContentBlockNewPage
} from '@admin/pages/personalization/content-block.tsx';
import { ContentBlockHubPage } from '@admin/pages/personalization/content-block-hub.tsx';
import { Games } from '@admin/pages/games/games.tsx';
import { Boosters } from '@admin/pages/boosters/boosters.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { getSafeRedirectPath } from '@r/utils/redirect.ts';
import PointsInsights from '@admin/pages/points/insights.tsx';
import { Performances } from '@admin/pages/performances/performances.tsx';
import { PurchaseProofs } from '@admin/pages/performances/purchase-proofs.tsx';
import { SettingsPage } from '@admin/pages/settings/settings.tsx';
import { PurchaseProofSettings } from '@admin/pages/settings/purchase-proofs.tsx';
import { canManagePurchaseProofSettings } from '@admin/pages/settings/purchase-proof-settings-utils.ts';
import { PersonalizationPage } from '@admin/pages/personalization/personalization.tsx';
import { PersonalizationAssetsPage } from '@admin/pages/personalization/assets.tsx';
import { LegalMediasPage } from '@admin/pages/personalization/legal-medias.tsx';
import { ExternalLinksPage } from '@admin/pages/personalization/external-links.tsx';
import { PersonalizationDisplayPage } from '@admin/pages/personalization/display.tsx';
import { CompanyWaitingList } from '@admin/pages/companies/waiting-list.tsx';
import { ShopsSettings } from '@admin/pages/shops/settings.tsx';
import { SettingsStepsListPage } from '@admin/pages/settings/steps-list.tsx';
import { Workspace } from '@admin/pages/workspace/workspace.tsx';
import { DomainConfiguration } from '@admin/pages/workspace/domain-configuration.tsx';
import { EmailConfiguration } from '@admin/pages/workspace/email-configuration.tsx';
import { VideoInstructions } from '@admin/pages/ressources/video-instructions.tsx';
import { VideoInstructionsLibrary } from '@admin/pages/ressources/video-instructions-library.tsx';
import { Changelogs } from '@admin/pages/ressources/changelogs.tsx';
import { ChangelogCreate } from '@admin/pages/ressources/changelog-create.tsx';
import { ChangelogsFeed } from '@admin/pages/changelogs/changelogs-feed.tsx';

const TanStackRouterDevtools =
  process.env.NODE_ENV === 'production'
    ? () => null // Render nothing in production
    : lazy(() =>
        // Lazy load in development
        import('@tanstack/react-router-devtools').then((res) => ({
          default: res.TanStackRouterDevtools
          // For Embedded Mode
          // default: res.TanStackRouterDevtoolsPanel
        }))
      );

export interface MyRouterContext {
  user: UserMeReadModel | null;
  routes: RouteRouteReadModel[];
  routesLoaded?: boolean;
  workSpaceCreateFormData: Partial<WorkspaceWorkspaceWriteModel> | null;
}

/** Indique si le chemin exact est listé dans GET `/routes` (droits menu admin). */
const isRoutePathAllowed = (routes: RouteRouteReadModel[], path: string): boolean =>
  routes.some((route) => route.path === path);

/**
 * Garde d’accès avant chargement de page : compare au catalogue `/routes`.
 * `fallbackPath` : autorise les sous-routes si la page parente est permise
 * (ex. `/content/content-block/new` avec fallback `/content/content-block`).
 */
const checkAndRedirect = (
  context: MyRouterContext,
  _location: ParsedLocation,
  path: string,
  fallbackPath?: string
) => {
  if (!context.routesLoaded) {
    return;
  }

  if (isRoutePathAllowed(context.routes, path)) {
    return;
  }

  if (fallbackPath && isRoutePathAllowed(context.routes, fallbackPath)) {
    return;
  }

  throw notFound();
};

const getAdminPageTitle = (pageTitle?: string) =>
  pageTitle ? `${pageTitle} | Fiddy Admin` : 'Fiddy Admin';

const getHead = (pageTitle?: string) => ({
  meta: [{ title: getAdminPageTitle(pageTitle) }]
});

const rootRoute = createRootRouteWithContext<MyRouterContext>()({
  head: () => getHead(),
  component: () => {
    return (
      <>
        <HeadContent />
        <Outlet />
        <TanStackRouterDevtools position={'bottom-right'} />
      </>
    );
  }
});

const PublicLayout = createRoute({
  getParentRoute: () => rootRoute,
  beforeLoad: ({ context, location }) => {
    if (localStorage.getItem('token') && context?.user?.id) {
      const redirectPath = getSafeRedirectPath(location.search);
      throw redirectPath ? redirect({ href: redirectPath }) : redirect({ to: '/' });
    }
  },
  id: 'public-layout',
  component: PublicLayoutComponent
});

const PublicLogin = createRoute({
  getParentRoute: () => PublicLayout,
  path: '/login',
  head: () => getHead('Connexion'),
  component: function Index() {
    return <Auth />;
  }
});

const AuthLayout = createRoute({
  getParentRoute: () => rootRoute,
  beforeLoad: ({ context, location }) => {
    if (!context.user || localStorage.getItem('token') === null) {
      throw redirect({
        to: '/login',
        search: {
          redirect: location.href
        }
      });
    }
    const activeWorkspace = getActiveWorkspaceUniversal(context.user?.visibleWorkspaces);

    // Vérifier si le workspace est disponible
    if (!activeWorkspace) {
      throw redirect({
        to: '/login',
        search: {
          error: 'workspace-unavailable'
        }
      });
    }

    const hasAdminRole = activeWorkspace?.roles?.includes('ROLE_ADMIN');
    const hasSalesAdminRole = activeWorkspace?.roles?.includes('ROLE_SALES_ADMIN');

    // Check if the active workspace is in draft mode before check if password need to be changed
    if (activeWorkspace?.workspace.status === 'draft') {
      throw redirect({
        to: '/create-workspace/step/1'
      });
    }
    if ((hasAdminRole || hasSalesAdminRole) && activeWorkspace?.passwordNeedToBeChanged) {
      throw redirect({
        to: '/change-password'
      });
    }
  },
  id: 'auth-layout',
  component: function Index() {
    return <Layout />;
  }
});

const AuthDashboard = createRoute({
  getParentRoute: () => AuthLayout,
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/');
  },
  path: '/',
  head: () => getHead('Tableau de bord'),
  component: function Index() {
    return <AdminDashboard />;
  }
});

const AuthUsers = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/users');
  },
  getParentRoute: () => AuthLayout,
  path: '/users',
  head: () => getHead('Mes contacts'),
  component: function Index() {
    return <UserList />;
  }
});

const AuthUsersInsights = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/users/insights');
  },
  getParentRoute: () => AuthLayout,
  path: '/users/insights',
  head: () => getHead('Statistiques contacts'),
  component: function Index() {
    return <UserInsights />;
  }
});

const AuthUsersImport = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/users/import');
  },
  getParentRoute: () => AuthLayout,
  path: '/users/import',
  head: () => getHead('Ajout de contacts'),
  component: function Index() {
    return <UserImport />;
  }
});

const AuthUser = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/users/$id');
  },
  getParentRoute: () => AuthLayout,
  path: '/users/$id',
  head: () => getHead('Détail contact'),
  component: function Index() {
    return <User />;
  }
});

const AuthCompaniesWaitingList = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/companies/waiting-list');
  },
  getParentRoute: () => AuthLayout,
  path: '/companies/waiting-list',
  head: () => getHead('Gestion des inscriptions'),
  component: function Index() {
    return <CompanyWaitingList />;
  }
});

const AuthCompaniesInsights = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/companies/insights');
  },
  getParentRoute: () => AuthLayout,
  path: '/companies/insights',
  head: () => getHead('Statistiques sociétés'),
  component: function Index() {
    return <CompanyInsights />;
  }
});

const AuthCompany = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/companies/$id');
  },
  getParentRoute: () => AuthLayout,
  path: '/companies/$id',
  head: () => getHead('Détail société'),
  component: function Index() {
    return <Company />;
  }
});

const AuthCompanies = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/companies');
  },
  getParentRoute: () => AuthLayout,
  path: '/companies',
  head: () => getHead('Mes sociétés'),
  component: function Index() {
    return <CompanyList />;
  }
});

const AuthCompaniesModify = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/companies/modify');
  },
  getParentRoute: () => AuthLayout,
  path: '/companies/modify',
  head: () => getHead('Modification sociétés'),
  component: function Index() {
    return <CompaniesModify />;
  }
});

const AuthcompaniesImport = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/companies/import');
  },
  getParentRoute: () => AuthLayout,
  path: '/companies/import',
  head: () => getHead('Ajout de sociétés'),
  component: function Index() {
    return <CompaniesImport />;
  }
});

const AuthTargetsImport = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/targets/import');
  },
  getParentRoute: () => AuthLayout,
  path: '/targets/import',
  head: () => getHead('Import objectifs'),
  component: function Index() {
    return <TargetsImport />;
  }
});

const AuthPoints = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/points');
  },
  getParentRoute: () => AuthLayout,
  path: '/points',
  head: () => getHead('Mes points'),
  component: function Index() {
    return <Points />;
  }
});

const AuthPointsInsights = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/points/insights');
  },
  getParentRoute: () => AuthLayout,
  path: '/points/insights',
  head: () => getHead('Statistiques points'),
  component: function Index() {
    return <PointsInsights />;
  }
});

const AuthPointsImport = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/points/import');
  },
  getParentRoute: () => AuthLayout,
  path: '/points/import',
  head: () => getHead('Ajout de points'),
  component: function Index() {
    return <PointsImport />;
  }
});

const AuthPerformances = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/performances');
  },
  getParentRoute: () => AuthLayout,
  path: '/performances',
  head: () => getHead('Mes performances'),
  component: function Index() {
    return <Performances />;
  }
});

const AuthPerformancesInsights = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/performances/insights');
  },
  getParentRoute: () => AuthLayout,
  path: '/performances/insights',
  head: () => getHead('Statistiques performances'),
  component: function Index() {
    return <PerformancesInsights />;
  }
});

const AuthPerformancesImport = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/performances/import');
  },
  getParentRoute: () => AuthLayout,
  path: '/performances/import',
  head: () => getHead('Ajout de performances'),
  component: function Index() {
    return <PerformancesImport />;
  }
});

const AuthProductsReferences = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/performances/products-references');
  },
  getParentRoute: () => AuthLayout,
  path: '/performances/products-references',
  head: () => getHead('Référentiel produits'),
  component: function Index() {
    return <ProductsReferences />;
  }
});

const AuthPurchaseProofs = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/performances/purchase-proofs');
  },
  getParentRoute: () => AuthLayout,
  path: '/performances/purchase-proofs',
  head: () => getHead("Justificatifs d'achats"),
  component: function Index() {
    return <PurchaseProofs />;
  }
});

const AuthTargets = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/performances/targets');
  },
  getParentRoute: () => AuthLayout,
  path: '/performances/targets',
  head: () => getHead('Mes objectifs'),
  component: function Index() {
    return <Targets />;
  }
});

const AuthSteps = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/performances/steps');
  },
  getParentRoute: () => AuthLayout,
  path: '/performances/steps',
  head: () => getHead('Mes paliers'),
  component: function Index() {
    return <Steps />;
  }
});

const AuthSettings = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/settings');
  },
  getParentRoute: () => AuthLayout,
  path: '/settings',
  head: () => getHead('Paramètres'),
  component: function Index() {
    return <SettingsPage />;
  }
});

const AuthFeaturesSettings = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/settings/features');
    const activeWorkspace = getActiveWorkspaceUniversal(context.user?.visibleWorkspaces ?? []);
    const roles = activeWorkspace?.roles ?? [];

    if (!canManagePurchaseProofSettings(roles)) {
      throw redirect({ to: '/settings' });
    }
  },
  getParentRoute: () => AuthLayout,
  path: '/settings/features',
  head: () => getHead('Fonctionnalités'),
  component: PurchaseProofSettings
});

const AuthApiToken = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/settings/api/tokens');
  },
  getParentRoute: () => AuthLayout,
  path: '/settings/api/tokens',
  head: () => getHead('Tokens API'),
  component: function Index() {
    return <ApiToken />;
  }
});

const AuthApiTokenDoc = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/settings/api/tokens');
  },
  getParentRoute: () => rootRoute,
  path: '/settings/api/tokens/doc',
  head: () => getHead('Documentation API'),
  component: function Index() {
    return <ApiTokenDoc />;
  }
});

const AuthTypology = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/settings/typologies');
  },
  getParentRoute: () => AuthLayout,
  path: '/settings/typologies',
  head: () => getHead('Typologies'),
  component: function Index() {
    return <Typologies />;
  }
});

const AuthSegments = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/settings/segments');
  },
  getParentRoute: () => AuthLayout,
  path: '/settings/segments',
  head: () => getHead('Segments'),
  component: function Index() {
    return <Segments />;
  }
});

const AuthHierarchy = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/settings/hierarchy');
  },
  getParentRoute: () => AuthLayout,
  path: '/settings/hierarchy',
  head: () => getHead('Hiérarchisation'),
  component: function Index() {
    return <Hierarchy />;
  }
});

const AuthHierarchyDetail = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/settings/hierarchy/$id');
  },
  getParentRoute: () => AuthLayout,
  path: '/settings/hierarchy/$id',
  head: () => getHead('Hiérarchie'),
  component: function Index() {
    return <HierarchyDetail />;
  }
});

const AuthSegmentEdit = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/settings/segments/$id');
  },
  getParentRoute: () => AuthLayout,
  path: '/settings/segments/$id',
  head: () => getHead('Modifier un segment'),
  component: function Index() {
    return <SegmentEdit />;
  }
});

const AuthShopsSettings = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/settings/shops/settings');
  },
  getParentRoute: () => AuthLayout,
  path: '/settings/shops/settings',
  head: () => getHead('Paramètres boutiques'),
  component: function Index() {
    return <ShopsSettings />;
  }
});

const AuthWorkspace = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/settings/my-workspace');
  },
  getParentRoute: () => AuthLayout,
  path: '/settings/my-workspace',
  head: () => getHead('Informations programme'),
  component: function Index() {
    return <Workspace />;
  }
});

const AuthWorkspaceDomainConfiguration = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/settings/my-workspace/domain-configuration');
  },
  getParentRoute: () => AuthLayout,
  path: '/settings/my-workspace/domain-configuration',
  head: () => getHead('Configuration du nom de domaine'),
  component: DomainConfiguration
});

const AuthWorkspaceEmailConfiguration = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/settings/my-workspace/email-configuration');
  },
  getParentRoute: () => AuthLayout,
  path: '/settings/my-workspace/email-configuration',
  head: () => getHead('Configuration des adresses e-mail'),
  component: EmailConfiguration
});

const AuthSettingsSteps = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/settings/steps');
  },
  getParentRoute: () => AuthLayout,
  path: '/settings/steps',
  head: () => getHead('Liste des paliers'),
  component: function Index() {
    return <SettingsStepsListPage />;
  }
});

const AuthPersonalization = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/personalization');
  },
  getParentRoute: () => AuthLayout,
  path: '/personalization',
  head: () => getHead('Personnalisation'),
  component: function Index() {
    return <PersonalizationPage />;
  }
});

const AuthLegalMedias = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/personalization/legal');
  },
  getParentRoute: () => AuthLayout,
  path: '/personalization/legal',
  head: () => getHead('Documents légaux'),
  component: function Index() {
    return <LegalMediasPage />;
  }
});

const AuthTranslations = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/personalization/translations');
  },
  getParentRoute: () => AuthLayout,
  path: '/personalization/translations',
  head: () => getHead('Traductions'),
  component: function Index() {
    return <Translations />;
  }
});

const AuthExternalLinks = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/personalization/external-links');
  },
  getParentRoute: () => AuthLayout,
  path: '/personalization/external-links',
  head: () => getHead('Liens externes'),
  component: function Index() {
    return <ExternalLinksPage />;
  }
});

const AuthPersonalizationDisplay = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/personalization/display');
  },
  getParentRoute: () => AuthLayout,
  path: '/personalization/display',
  head: () => getHead("Personnaliser l'affichage"),
  component: function Index() {
    return <PersonalizationDisplayPage />;
  }
});

const AuthPersonalizationAssets = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/personalization/assets');
    const activeWorkspace = getActiveWorkspaceUniversal(context.user?.visibleWorkspaces ?? []);
    const roles = activeWorkspace?.roles ?? [];
    const canAccess = roles.includes('ROLE_ADMIN') || roles.includes('ROLE_FIDDY_ADMIN');
    if (!canAccess) {
      throw redirect({ to: '/personalization' });
    }
  },
  getParentRoute: () => AuthLayout,
  path: '/personalization/assets',
  head: () => getHead('Gestion des images'),
  component: function Index() {
    return <PersonalizationAssetsPage />;
  }
});

const AuthScheduledTasks = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/settings/scheduled-tasks');
  },
  getParentRoute: () => AuthLayout,
  path: '/settings/scheduled-tasks',
  head: () => getHead('Automatisations'),
  component: function Index() {
    return <ScheduledTasks />;
  }
});

const AuthShops = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/shops');
  },
  getParentRoute: () => AuthLayout,
  path: '/shops',
  head: () => getHead('Boutiques'),
  component: function Index() {
    return <Shops />;
  }
});

const AuthShop = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/shops/$id');
  },
  getParentRoute: () => AuthLayout,
  path: '/shops/$id',
  head: () => getHead('Détail boutique'),
  component: function Index() {
    return <Shop />;
  }
});

const AuthOrders = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/shops/orders');
  },
  getParentRoute: () => AuthLayout,
  path: '/shops/orders',
  head: () => getHead('Suivi des commandes'),
  component: Orders
});

const AuthOrdersInsights = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/shops/orders/insights');
  },
  getParentRoute: () => AuthLayout,
  path: '/shops/orders/insights',
  head: () => getHead('Statistiques commandes'),
  component: OrdersInsights
});

const AuthOrder = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/shops/orders/$id');
  },
  getParentRoute: () => AuthLayout,
  path: '/shops/orders/$id',
  head: () => getHead('Détail commande'),
  component: () => <Order admin={true} />
});

const AuthOrderRewardDetail = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/shops/orders/$id/reward-obtained/$productId');
  },
  getParentRoute: () => AuthLayout,
  path: '/shops/orders/$id/reward-obtained/$productId',
  head: () => getHead('Détail récompense'),
  component: OrderRewardDetail
});

const AuthMyAccount = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/my-account');
  },
  getParentRoute: () => AuthLayout,
  path: '/my-account',
  head: () => getHead('Mon compte'),
  component: function Index() {
    return <MyAccount />;
  }
});

const PublicResetPassword = createRoute({
  getParentRoute: () => PublicLayout,
  path: 'reset-password'
});

const PublicResetPasswordIndex = createRoute({
  getParentRoute: () => PublicResetPassword,
  path: '/',
  head: () => getHead('Réinitialisation du mot de passe'),
  component: ResetPassword
});

const PublicResetPasswordCallback = createRoute({
  getParentRoute: () => PublicResetPassword,
  path: '/$token',
  head: () => getHead('Nouveau mot de passe'),
  component: ResetPasswordCallback
});

const PublicLayoutFirstLoginChangePassword = createRoute({
  getParentRoute: () => rootRoute,
  id: 'public-layout-first-login-change-password',
  component: PublicLayoutComponent
});

const PublicFirstLoginChangePassword = createRoute({
  getParentRoute: () => PublicLayoutFirstLoginChangePassword,
  beforeLoad: ({ context }) => {
    const token = localStorage.getItem('token');
    const user = context?.user;
    if (token && user) {
      const activeWorkspace = user.visibleWorkspaces?.find((workspace) => workspace.active);
      const hasAdminRole = activeWorkspace?.roles?.includes('ROLE_ADMIN');
      const hasSalesAdminRole = activeWorkspace?.roles?.includes('ROLE_SALES_ADMIN');
      if ((hasAdminRole || hasSalesAdminRole) && activeWorkspace?.passwordNeedToBeChanged) {
        return;
      }
      throw redirect({
        to: '/'
      });
    }
    throw redirect({
      to: '/login'
    });
  },
  path: '/change-password',
  head: () => getHead('Changement de mot de passe'),
  component: function Index() {
    return <ChangePassword />;
  }
});

const PublicCreateWorkspace = createRoute({
  getParentRoute: () => rootRoute,
  path: '/create-workspace',
  component: CreateWorkspaceLayout,
  beforeLoad: ({ context }) => {
    const activeWorkspace = getActiveWorkspaceUniversal(context.user?.visibleWorkspaces);
    if (activeWorkspace?.workspace.status !== 'draft') {
      throw redirect({
        to: '/'
      });
    }
  }
});

const PublicCreateWorkspaceStep1 = createRoute({
  getParentRoute: () => PublicCreateWorkspace,
  path: 'step/1',
  head: () => getHead('Création workspace - Étape 1'),
  component: CreateWorkspaceStep1
});

const PublicCreateWorkspaceStep2 = createRoute({
  getParentRoute: () => PublicCreateWorkspace,
  path: 'step/2',
  head: () => getHead('Création workspace - Étape 2'),
  component: CreateWorkspaceStep2,
  beforeLoad: ({ context }) => {
    if (context.workSpaceCreateFormData) {
      if (!getStepValidation(1, context.workSpaceCreateFormData)) {
        throw redirect({
          to: '/create-workspace/step/1'
        });
      }
    }
    if (!context.workSpaceCreateFormData) {
      throw redirect({
        to: '/create-workspace/step/1'
      });
    }
  }
});

const PublicCreateWorkspaceStep3 = createRoute({
  getParentRoute: () => PublicCreateWorkspace,
  path: 'step/3',
  head: () => getHead('Création workspace - Étape 3'),
  beforeLoad: ({ context }) => {
    if (context.workSpaceCreateFormData) {
      if (
        !getStepValidation(1, context.workSpaceCreateFormData) ||
        !getStepValidation(2, context.workSpaceCreateFormData)
      ) {
        throw redirect({
          to: '/create-workspace/step/2'
        });
      }
    }
    if (!context.workSpaceCreateFormData) {
      throw redirect({
        to: '/create-workspace/step/1'
      });
    }
  },
  component: CreateWorkspaceStep3
});

const PublicCreateWorkspaceStep4 = createRoute({
  getParentRoute: () => PublicCreateWorkspace,
  path: 'step/4',
  head: () => getHead('Création workspace - Étape 4'),
  beforeLoad: ({ context }) => {
    if (context.workSpaceCreateFormData) {
      if (
        !getStepValidation(1, context.workSpaceCreateFormData) ||
        !getStepValidation(2, context.workSpaceCreateFormData) ||
        !getStepValidation(3, context.workSpaceCreateFormData)
      ) {
        throw redirect({
          to: '/create-workspace/step/3'
        });
      }
    }
    if (!context.workSpaceCreateFormData) {
      throw redirect({
        to: '/create-workspace/step/1'
      });
    }
  },
  component: CreateWorkspaceStep4
});

const FiddyAdminWorkspace = createRoute({
  beforeLoad: ({ context, location }) => {
    const user = context.user;
    const activeWorkspace = getActiveWorkspaceUniversal(user?.visibleWorkspaces);
    const isSuperAdmin = activeWorkspace?.roles?.includes('ROLE_FIDDY_ADMIN');

    if (!isSuperAdmin) {
      throw redirect({
        to: '/'
      });
    }

    checkAndRedirect(context, location, '/fiddy-admin');
  },
  getParentRoute: () => AuthLayout,
  path: '/fiddy-admin',
  head: () => getHead('Fiddy Mega Admin'),
  component: function Index() {
    return <FiddyAdminDashboard />;
  }
});

const AuthVideoInstructionsLibrary = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/ressources/video-instructions');
  },
  getParentRoute: () => AuthLayout,
  path: '/ressources/video-instructions',
  head: () => getHead('Notices Vidéo'),
  component: function Index() {
    return <VideoInstructionsLibrary />;
  }
});

const AuthAdminTranslations = createRoute({
  beforeLoad: ({ context, location }) => {
    const user = context.user;
    const activeWorkspace = getActiveWorkspaceUniversal(user?.visibleWorkspaces);
    const isSuperAdmin = activeWorkspace?.roles?.includes('ROLE_FIDDY_ADMIN');

    if (!isSuperAdmin) {
      throw redirect({
        to: '/'
      });
    }

    checkAndRedirect(context, location, '/fiddy-admin');
  },
  getParentRoute: () => AuthLayout,
  path: '/fiddy-admin/translations',
  head: () => getHead('Traductions admin'),
  component: function Index() {
    return <AdminTranslations />;
  }
});

const AuthVideoInstructions = createRoute({
  beforeLoad: ({ context, location }) => {
    const activeWorkspace = getActiveWorkspaceUniversal(context.user?.visibleWorkspaces);
    const isFiddyAdmin = activeWorkspace?.roles?.includes('ROLE_FIDDY_ADMIN');

    if (!isFiddyAdmin) {
      throw redirect({
        to: '/'
      });
    }

    checkAndRedirect(context, location, '/fiddy-admin/ressources/video-instructions');
  },
  getParentRoute: () => AuthLayout,
  path: '/fiddy-admin/ressources/video-instructions',
  head: () => getHead('Notices Vidéo'),
  component: function Index() {
    return <VideoInstructions />;
  }
});

const AuthChangelogs = createRoute({
  beforeLoad: ({ context, location }) => {
    const activeWorkspace = getActiveWorkspaceUniversal(context.user?.visibleWorkspaces);
    const isFiddyAdmin = activeWorkspace?.roles?.includes('ROLE_FIDDY_ADMIN');

    if (!isFiddyAdmin) {
      throw redirect({
        to: '/'
      });
    }

    checkAndRedirect(context, location, '/fiddy-admin/ressources/changelogs');
  },
  getParentRoute: () => AuthLayout,
  path: '/fiddy-admin/ressources/changelogs',
  head: () => getHead('Gestion des nouveautés'),
  component: function Index() {
    return <Changelogs />;
  }
});

const AuthChangelogCreate = createRoute({
  beforeLoad: ({ context, location }) => {
    const activeWorkspace = getActiveWorkspaceUniversal(context.user?.visibleWorkspaces);
    const isFiddyAdmin = activeWorkspace?.roles?.includes('ROLE_FIDDY_ADMIN');

    if (!isFiddyAdmin) {
      throw redirect({
        to: '/'
      });
    }

    checkAndRedirect(context, location, '/fiddy-admin/ressources/changelogs/new');
  },
  getParentRoute: () => AuthLayout,
  path: '/fiddy-admin/ressources/changelogs/new',
  head: () => getHead('Rédiger une nouveauté'),
  component: function Index() {
    return <ChangelogCreate />;
  }
});

const AuthChangelogsFeed = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/changelogs');
  },
  getParentRoute: () => AuthLayout,
  path: '/changelogs',
  head: () => getHead('Nouveautés Fiddy'),
  component: function Index() {
    return <ChangelogsFeed />;
  }
});

const AuthContentBlock = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/content/content-block');
  },
  getParentRoute: () => AuthLayout,
  path: '/content/content-block',
  head: () => getHead('Blocs de contenu'),
  component: function Index() {
    return <ContentBlockHubPage />;
  }
});

const AuthContentBlockNew = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/content/content-block/new', '/content/content-block');
  },
  getParentRoute: () => AuthLayout,
  path: '/content/content-block/new',
  head: () => getHead('Blocs de contenu'),
  validateSearch: (search: Record<string, unknown>) => ({
    pageId: search.pageId === 'shop' ? ('shop' as const) : ('home' as const),
    widgetId: Number(search.widgetId) || 0
  }),
  component: function Index() {
    return <ContentBlockNewPage />;
  }
});

const AuthContentBlockEdit = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(
      context,
      location,
      '/content/content-block/$workspaceId',
      '/content/content-block'
    );
  },
  getParentRoute: () => AuthLayout,
  path: '/content/content-block/$workspaceId',
  head: () => getHead('Blocs de contenu'),
  component: function Index() {
    return <ContentBlockEditPage />;
  }
});

const AuthGames = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/games');
  },
  getParentRoute: () => AuthLayout,
  path: '/games',
  head: () => getHead('Animations'),
  component: function Index() {
    return <Games />;
  }
});

const AuthBoosters = createRoute({
  beforeLoad: ({ context, location }) => {
    checkAndRedirect(context, location, '/boosters');
  },
  getParentRoute: () => AuthLayout,
  path: '/boosters',
  head: () => getHead('Boosters'),
  component: function Index() {
    return <Boosters />;
  }
});

const routeTree = rootRoute.addChildren([
  AuthDashboard,
  AuthLayout,
  PublicLayout,
  PublicLogin,
  AuthUsersImport,
  AuthUsersInsights,
  AuthUser,
  AuthPoints,
  AuthPointsInsights,
  AuthPointsImport,
  AuthcompaniesImport,
  AuthTargetsImport,
  AuthCompanies,
  AuthCompany,
  AuthCompaniesModify,
  AuthCompaniesWaitingList,
  AuthCompaniesInsights,
  AuthPerformancesImport,
  AuthPerformances,
  AuthPerformancesInsights,
  AuthProductsReferences,
  AuthPurchaseProofs,
  AuthTargets,
  AuthSteps,
  AuthUsers,
  AuthSettings,
  AuthFeaturesSettings,
  AuthApiToken,
  AuthApiTokenDoc,
  AuthTypology,
  AuthSegments,
  AuthHierarchy,
  AuthHierarchyDetail,
  AuthSegmentEdit,
  AuthShopsSettings,
  AuthWorkspace,
  AuthWorkspaceDomainConfiguration,
  AuthWorkspaceEmailConfiguration,
  AuthSettingsSteps,
  AuthTranslations,
  AuthScheduledTasks,
  AuthShops,
  AuthShop,
  AuthOrders,
  AuthOrdersInsights,
  AuthOrder,
  AuthOrderRewardDetail,
  AuthGames,
  AuthBoosters,
  AuthMyAccount,
  PublicLayoutFirstLoginChangePassword,
  PublicFirstLoginChangePassword,
  PublicResetPassword,
  PublicResetPasswordCallback,
  PublicResetPasswordIndex,
  PublicCreateWorkspace,
  PublicCreateWorkspaceStep1,
  PublicCreateWorkspaceStep2,
  PublicCreateWorkspaceStep3,
  PublicCreateWorkspaceStep4,
  FiddyAdminWorkspace,
  AuthVideoInstructionsLibrary,
  AuthAdminTranslations,
  AuthVideoInstructions,
  AuthChangelogs,
  AuthChangelogCreate,
  AuthChangelogsFeed,
  AuthContentBlock,
  AuthContentBlockNew,
  AuthContentBlockEdit,
  AuthPersonalization,
  AuthPersonalizationDisplay,
  AuthPersonalizationAssets,
  AuthLegalMedias,
  AuthExternalLinks
]);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof adminRouter;
  }
}

export const adminRouter = createRouter({
  defaultNotFoundComponent: () => {
    return <NotFoundError />;
  },
  routeTree,
  defaultPreload: 'intent',
  notFoundMode: 'root',
  context: {
    user: {} as UserMeReadModel,
    routes: [] as RouteRouteReadModel[],
    routesLoaded: false,
    workSpaceCreateFormData: {} as Partial<WorkspaceWorkspaceWriteModel>
  },
  defaultErrorComponent: ({ error }) => {
    return <InternalServerError error={error} />;
  }
});

// declare module '@tanstack/react-router' {
//   interface Register {
//     router: typeof adminRouter;
//   }
// }
